"use client";

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { useState, type ReactNode } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  TableEmptyState,
  type TableEmptyStateProps,
} from "@/components/ui/table-empty-state";
import { type ListingDetailedType } from "@/hooks/use-listings";

interface ListingsTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  defaultSorting?: SortingState;
  isLoading?: boolean;
  emptyMessage?: string;
  emptyContent?: ReactNode;
  emptyState?: TableEmptyStateProps;
}

export function ListingsTable<TData extends ListingDetailedType>({
  data,
  columns,
  defaultSorting = [],
  isLoading,
  emptyMessage = "No listings found.",
  emptyContent,
  emptyState,
}: ListingsTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);

  // Create table instance
  const columnIds = columns
    .map((c) =>
      c?.id
        ? String(c.id)
        : "accessorKey" in (c as any) && (c as any).accessorKey
          ? String((c as any).accessorKey)
          : undefined,
    )
    .filter(Boolean) as string[];

  const effectiveSorting = sorting.filter((s) => columnIds.includes(s.id));

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // Prevent auto-resets that can cause setState loops when data refs are unstable during loading
    autoResetPageIndex: false,
    autoResetAll: false,
    // Ensure stable row identity so UI elements (like open menus) don't jump between rows
    getRowId: (originalRow: TData, index: number) => {
      const id = (originalRow as unknown as { id?: string | number })?.id;
      return typeof id === "string" || typeof id === "number"
        ? String(id)
        : String(index);
    },
    onSortingChange: setSorting,
    state: {
      sorting: effectiveSorting,
    },
  });

  return (
    <div className="" role="region" aria-label="Listings table">
      <Table className="table-auto">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                const headerClassName =
                  header.column.columnDef?.meta?.headerClassName ?? "";
                return (
                  <TableHead
                    key={header.id}
                    scope="col"
                    className={`${index === 0 ? "pl-0" : ""} ${headerClassName}`}
                    onClick={header.column.getToggleSortingHandler?.()}
                    role="columnheader"
                    aria-sort={
                      header.column.getIsSorted() === "asc"
                        ? "ascending"
                        : header.column.getIsSorted() === "desc"
                          ? "descending"
                          : "none"
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, _rowIndex) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                className="border-b bg-background sm:h-16"
              >
                {row.getVisibleCells().map((cell, cellIndex) => {
                  const cellClassName =
                    cell.column.columnDef?.meta?.cellClassName ?? "";
                  const isLastCell =
                    cellIndex === row.getVisibleCells().length - 1;
                  const isFirstCell = cellIndex === 0;
                  return (
                    <TableCell
                      key={cell.id}
                      role={cellIndex === 0 ? "rowheader" : undefined}
                      className={`${isFirstCell ? "pl-2" : ""} ${
                        isLastCell ? "justify-end pr-2 text-right" : ""
                      } ${
                        !isFirstCell && !isLastCell ? "pl-5" : ""
                      } whitespace-normal sm:whitespace-nowrap ${cellClassName}`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : !isLoading ? (
            <TableRow className="border-b hover:!bg-transparent">
              <TableCell
                colSpan={columns.length}
                className="py-12 text-center"
                role="status"
              >
                {emptyState ? (
                  <TableEmptyState
                    title={emptyState.title}
                    description={emptyState.description}
                    image={emptyState.image}
                    action={emptyState.action}
                  />
                ) : (
                  (emptyContent ?? emptyMessage)
                )}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </div>
  );
}
